import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from "amazon-cognito-identity-js";

/**
 * Cognito sign-in for the standalone build.
 *
 * Standalone-only: inside New Relic the platform already authenticates the
 * viewer, so nothing here is copied into the nerdpack.
 *
 * `authenticateUser` uses SRP (Secure Remote Password), so the password is
 * never transmitted — only a zero-knowledge proof derived from it. The app
 * client must therefore be a public SPA client with NO client secret; a secret
 * cannot be kept safe in browser code and the SDK will reject the flow.
 *
 * NOTE ON SCOPE: this gates the *UI*, not the files. The bundle and any data
 * the browser fetches remain publicly reachable on CloudFront. Restricting the
 * content itself needs signed cookies / Lambda@Edge in front of the origin, or
 * an API that validates the Cognito JWT server-side.
 */

let pool = null;

export function configureCognito({ userPoolId, clientId } = {}) {
  if (!userPoolId || !clientId) {
    pool = null;
    return null;
  }
  pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
  return pool;
}

export const isCognitoConfigured = () => pool !== null;

const userFor = (email) => new CognitoUser({ Username: email, Pool: pool });

/** Shapes a Cognito session into the bits the UI needs. */
function toSession(cognitoSession, username) {
  const payload = cognitoSession.getIdToken().decodePayload();
  return {
    username: payload.email || payload["cognito:username"] || username,
    email: payload.email || null,
    groups: payload["cognito:groups"] || [],
    idToken: cognitoSession.getIdToken().getJwtToken(),
    accessToken: cognitoSession.getAccessToken().getJwtToken(),
    expiresAt: payload.exp ? payload.exp * 1000 : null,
  };
}

/**
 * Restores a session from a previous visit. The SDK persists tokens in
 * localStorage and refreshes them here if the id token has expired.
 */
export function restoreSession() {
  return new Promise((resolve) => {
    if (!pool) return resolve(null);

    const user = pool.getCurrentUser();
    if (!user) return resolve(null);

    user.getSession((error, session) => {
      if (error || !session?.isValid()) return resolve(null);
      resolve(toSession(session, user.getUsername()));
    });
  });
}

export class AuthError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

/** Maps Cognito's error codes to something a user can act on. */
function friendlyError(error) {
  const code = error?.code || error?.name;
  switch (code) {
    case "NotAuthorizedException":
      return new AuthError("Incorrect email or password.", code);
    case "UserNotFoundException":
      // Deliberately identical to the above so this cannot be used to probe
      // which addresses are registered.
      return new AuthError("Incorrect email or password.", code);
    case "PasswordResetRequiredException":
      return new AuthError(
        "A password reset is required. Please contact your administrator.",
        code
      );
    case "UserNotConfirmedException":
      return new AuthError("This account has not been confirmed yet.", code);
    case "TooManyRequestsException":
    case "LimitExceededException":
      return new AuthError("Too many attempts. Please wait and try again.", code);
    case "NetworkError":
      return new AuthError("Could not reach Cognito. Check your connection.", code);
    default:
      return new AuthError(error?.message || "Sign-in failed.", code);
  }
}

/**
 * @returns {Promise<{status:'SUCCESS', session:object}
 *                 | {status:'NEW_PASSWORD_REQUIRED', user:object}>}
 */
export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    if (!pool) {
      return reject(new AuthError("Cognito is not configured.", "NOT_CONFIGURED"));
    }

    const user = userFor(email);

    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) =>
          resolve({ status: "SUCCESS", session: toSession(session, email) }),

        onFailure: (error) => reject(friendlyError(error)),

        // Admin-created users must set their own password on first sign-in.
        newPasswordRequired: () => resolve({ status: "NEW_PASSWORD_REQUIRED", user }),

        // Surfaced rather than half-implemented, so it fails loudly instead of
        // hanging on a challenge the UI cannot answer.
        mfaRequired: () =>
          reject(
            new AuthError(
              "This account requires MFA, which this sign-in form does not support yet.",
              "MFA_REQUIRED"
            )
          ),
        totpRequired: () =>
          reject(
            new AuthError(
              "This account requires an authenticator code, which this sign-in form does not support yet.",
              "TOTP_REQUIRED"
            )
          ),
      }
    );
  });
}

/** Completes the first-sign-in password change. */
export function completeNewPassword(user, newPassword) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) =>
          resolve({ status: "SUCCESS", session: toSession(session, user.getUsername()) }),
        onFailure: (error) => reject(friendlyError(error)),
      }
    );
  });
}

export function signOut() {
  const user = pool?.getCurrentUser();
  if (user) user.signOut();
}
